import React, { useEffect, useRef } from 'react';
import { motion } from 'motion/react';

export interface MotionEngineProps {
  theme: 'superhero' | 'dark_knight' | 'wonderland';
}

export function AtmosphericMotionEngine({ theme }: MotionEngineProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    // Particle definitions based on theme
    const particleCount = theme === 'superhero' ? 60 : theme === 'dark_knight' ? 30 : theme === 'wonderland' ? 100 : 25;
    const particles = Array.from({ length: particleCount }).map((_, i) => {
      let type = 'standard';
      if (theme === 'wonderland') {
         if (i < 15) type = 'balloon';
         else if (i < 95) type = 'dust';
         else type = 'star';
      } else if (theme === 'dark_knight') {
         type = 'fog';
      }

      // Sizes
      let size = Math.random() * 4 + 1;
      if (type === 'balloon') size = Math.random() * 8 + 6;
      if (type === 'fog') size = Math.random() * 20 + 10;
      if (type === 'dust') size = Math.random() * 2 + 0.5;
      if (type === 'star') size = Math.random() * 1.5 + 0.5;

      // Velocities
      let vx = (Math.random() - 0.5) * 0.8;
      let vy = (Math.random() - 0.5) * 0.8;
      
      if (type === 'balloon') {
         vy = -Math.random() * 1.5 - 0.5;
      } else if (type === 'fog') {
         vx = (Math.random() - 0.5) * 1.5;
         vy = (Math.random() - 0.5) * 0.5;
      } else if (type === 'dust') {
         vx = (Math.random() - 0.5) * 0.5;
         vy = -Math.random() * 0.8 - 0.2;
      } else if (type === 'star') {
         vx = Math.random() * 6 + 3; // Fast right
         vy = Math.random() * 3 + 1; // Fast down
      }

      // Colors
      let color = '#fff';
      if (theme === 'superhero') {
         color = Math.random() > 0.5 ? '#0ea5e9' : '#ffd700';
      } else if (theme === 'dark_knight') {
         color = Math.random() > 0.5 ? '#cbd5e1' : '#94a3b8';
      } else if (theme === 'wonderland') {
         if (type === 'balloon') {
             color = Math.random() > 0.33 ? '#f9a8d4' : Math.random() > 0.5 ? '#a7f3d0' : '#fef08a';
         } else if (type === 'dust') {
             color = Math.random() > 0.5 ? '#fbcfe8' : '#e0e7ff'; // Pinkish or bluish dust
         } else if (type === 'star') {
             color = '#ffffff';
         }
      }

      // Alphas
      let alpha = Math.random() * 0.7 + 0.3;
      if (type === 'fog') alpha = Math.random() * 0.2 + 0.1;

      return { x: Math.random() * width, y: Math.random() * height, size, vx, vy, alpha, color, type };
    });

    let animationFrameId: number;

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.type === 'balloon') p.x += Math.sin(Date.now() / 1000 + p.size) * 0.5;
        if (p.type === 'dust') p.x += Math.sin(Date.now() / 500 + p.size) * 0.2;

        if (p.type === 'star') {
           if (p.x > width || p.y > height) {
              p.x = Math.random() * width - width / 2;
              p.y = -50;
           }
        } else {
           if (p.x < -100) p.x = width + 100;
           if (p.x > width + 100) p.x = -100;
           if (p.y < -100) p.y = height + 100;
           if (p.y > height + 100) p.y = -100;
        }

        ctx.save();
        
        if (p.type === 'balloon') {
          // Draw Balloon
          ctx.beginPath();
          ctx.ellipse(p.x, p.y, p.size * 1.8, p.size * 2.4, 0, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = p.alpha;
          ctx.shadowBlur = 15;
          ctx.shadowColor = p.color;
          ctx.fill();
          
          ctx.beginPath();
          ctx.moveTo(p.x, p.y + p.size * 2.3);
          ctx.lineTo(p.x - p.size * 0.6, p.y + p.size * 3);
          ctx.lineTo(p.x + p.size * 0.6, p.y + p.size * 3);
          ctx.closePath();
          ctx.fill();

          ctx.beginPath();
          ctx.moveTo(p.x, p.y + p.size * 3);
          ctx.quadraticCurveTo(p.x + p.size, p.y + p.size * 6, p.x - p.size * 0.5, p.y + p.size * 10);
          ctx.strokeStyle = `rgba(255,255,255,${p.alpha * 0.6})`;
          ctx.lineWidth = 1.5;
          ctx.shadowBlur = 0;
          ctx.stroke();

        } else if (p.type === 'fog') {
          // Draw Fog
          ctx.beginPath();
          const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 8);
          gradient.addColorStop(0, `${p.color}${Math.floor(p.alpha * 255).toString(16).padStart(2, '0')}`);
          gradient.addColorStop(1, `${p.color}00`);
          ctx.fillStyle = gradient;
          ctx.globalAlpha = p.alpha;
          ctx.arc(p.x, p.y, p.size * 8, 0, Math.PI * 2);
          ctx.fill();

        } else if (p.type === 'star') {
          // Draw Shooting Star
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - p.vx * 15, p.y - p.vy * 15);
          ctx.strokeStyle = `rgba(255,255,255,${p.alpha})`;
          ctx.lineWidth = p.size;
          ctx.stroke();
          
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = '#fff';
          ctx.shadowBlur = 12;
          ctx.shadowColor = '#fff';
          ctx.fill();
        } else if (p.type === 'dust') {
          // Draw Twinkling Fairy Dust
          const twinkle = (Math.sin(Date.now() / 200 + p.x) + 1) / 2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = p.alpha * twinkle * 0.8;
          ctx.shadowBlur = 10;
          ctx.shadowColor = p.color;
          ctx.fill();
        } else {
          // Standard Particle
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = p.alpha;
          ctx.shadowBlur = 12;
          ctx.shadowColor = p.color;
          ctx.fill();
        }
        
        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [theme]);

  return (
    <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden isolate">
      {/* 60fps HTML5 Canvas Particle Engine */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Volumetric Radial Aura Pulse (Glowing lights/candles) */}
      <div
        className="absolute inset-0 pointer-events-none animate-pulse"
        style={{
          animationDuration: theme === 'dark_knight' ? '3s' : '2s',
          background:
            theme === 'superhero'
              ? 'radial-gradient(ellipse at 50% 30%, rgba(14,165,233,0.3) 0%, rgba(255,215,0,0.15) 50%, transparent 80%)'
              : theme === 'dark_knight'
              ? 'radial-gradient(ellipse at 50% 80%, rgba(255,180,80,0.35) 0%, rgba(255,100,0,0.15) 30%, transparent 70%), radial-gradient(ellipse at 30% 60%, rgba(255,200,100,0.2) 0%, transparent 50%)'
              : 'radial-gradient(ellipse at 50% 40%, rgba(249,168,212,0.35) 0%, rgba(167,243,208,0.2) 60%, transparent 90%)',
        }}
      />
    </div>
  );
}
